import React, { useEffect, useState, useContext } from "react";
import { ThumbnailPreview, mapBackendTemplateToFrontend } from "components/CommonComponents/CertificatePreview";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Badge,
  Button,
  CardHeader,
  CardTitle,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  UncontrolledTooltip,
  FormGroup,
  Label,
} from "reactstrap";
import Select from "react-select";
import { createPortal } from "react-dom";
import AdminHeader from "components/Headers/AdminHeader";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getCertificateTemplateList, editCertificateTemplate, deleteCertificateTemplate } from "action/certificate";
import CustomPagination from "components/CommonComponents/ReactTable/CustomPagination";
import { certificateTemplateList } from "constants/certificate";
import { components } from "react-select";
import { fixedWidthCustomStyle } from "constants/CommonConstants";
import { STATUS_OPTIONS, CERTIFICATE_TEMPLATE_TYPE_OPTIONS } from "components/constant";
import { urlEncodedId } from "components/CommonComponents/encodeDecodeFunction";
import { showAlert, toCamelCase } from "components/CommonComponents/CommonFunctions";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";


const Templates = () => {
  const authData = JSON.parse(localStorage.getItem("p") || "{}");
  const role = authData?.role;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { notify } = useContext(NotificationContext);

  const [templateListState, setTemplateListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
  });
  const [showPreview, setShowPreview] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [pageNum, setPageNum] = useState(1);
  const [showStartModal, setShowStartModal] = useState(false);

  const [filterState, setFilterState] = useState({
    name: "",
    templateType: null,
    status: null,
    filterParams: [],
  });

  const [templateMeta, setTemplateMeta] = useState({ name: "", type: null });
  const [templateErrors, setTemplateErrors] = useState({ name: false, type: false });

  const certificateListData = useSelector(
    (state) => state.certificate.certificateTemplateList,
  );

  // const statusOptions = [
  //   { value: "ACTIVE", label: "Active" },
  //   { value: "INACTIVE", label: "Inactive" },
  // ];

  const buildFilterParams = () => {
    const filters = [];
    if (filterState.name?.trim()) {
      filters.push({
        field: "Name",
        condition: "like",
        filterValues: [filterState.name.trim()],
      });
    }

    if (filterState.templateType?.value) {
      filters.push({
        field: "Type",
        condition: "eq",
        filterValues: [filterState.templateType.value],
      });
    }

    if (filterState.status?.value) {
      filters.push({
        field: "Status",
        condition: "eq",
        filterValues: [filterState.status.value],
      });
    }
    return filters.length ? JSON.stringify(filters) : [];
  };

  const handleSearch = () => {
    const newFilters = buildFilterParams();
    setFilterState((prev) => ({
      ...prev,
      filterParams: newFilters,
    }));
    setPageNum(1);
  };

  const handleClear = () => {
    setFilterState({
      name: "",
      templateType: null,
      status: null,
      filterParams: [],
    });
    setPageNum(1);
  };

  // const handleKeyDown = (e) => {
  //   if (e.key === "Enter") {
  //     handleSearch();
  //   }
  // };

  const getSelectStyle = (hasError) => {
    const base = fixedWidthCustomStyle("100%");
    base.control = (provided, state) => ({
      ...provided,
      height: "2.875rem",
      minHeight: "2.875rem",
      boxShadow: "none",
      borderColor: hasError ? "#fb6340" : "#dee2e6",
      "&:hover": {
        borderColor: hasError ? "#fb6340" : "#dee2e6",
      },
    });
    base.menuPortal = (provided) => ({ ...provided, zIndex: 99999 });
    return base;
  };

  const applyCanvasSize = (sizeKey) => {
    const newErrors = { name: false, type: false };
    let hasError = false;

    if (!templateMeta.name.trim()) {
      newErrors.name = true;
      hasError = true;
    }
    if (!templateMeta.type) {
      newErrors.type = true;
      hasError = true;
    }

    if (hasError) {
      setTemplateErrors(newErrors);
      return;
    }

    setShowStartModal(false);
    navigate(
      role === "SUPER_ADMIN"
        ? "/super-admin/certificate/create-certificate"
        : "/admin/certificate/create-certificate",
      { state: { selectedSize: sizeKey, templateName: templateMeta.name.trim(), templateType: templateMeta.type } },
    );
  };

  const handleOpenStartModal = () => {
    setTemplateMeta({ name: "", type: null });
    setTemplateErrors({ name: false, type: false });
    setShowStartModal(true);
  };

  useEffect(() => {
    setTemplateListState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    getCertificateTemplateList(dispatch, pageNum, filterState.filterParams);

    return () => {
      dispatch({ type: certificateTemplateList, payload: {} });
    };
  }, [dispatch, pageNum, filterState.filterParams]);

  useEffect(() => {
    if (certificateListData?.code === 200) {
      setTemplateListState({
        isLoading: false,
        error: "",
        noRecords: certificateListData?.data?.CertificateTemplate?.length === 0,
      });
    } else if (certificateListData?.code || certificateListData?.message) {
      setTemplateListState({
        isLoading: false,
        error:
          certificateListData?.message ||
          "Failed to load certificate templates! Please try again.",
        noRecords: false,
      });
    }
  }, [certificateListData]);

  const handlePreviewCancel = () => {
    setShowPreview(false);
    setPreviewTemplate(null);
  };

  const handleViewClick = (parsedTemplate) => {
    setPreviewTemplate(parsedTemplate);
    setShowPreview(true);
  };

  const handleDeleteClick = (item) => {
    showAlert({
      title: "Are you sure you want to delete this template?",
      message: `Template "${item.Name || item.name || "Untitled"}" will be deleted.`,
      type: "warning",
      confirmStyle: "deep-blue",
      cancelStyle: "secondary",
      confirmText: "Yes",
      cancelText: "Cancel",
      onConfirm: async (closeAlert) => {
        closeAlert();
        try {
          const res = await deleteCertificateTemplate(item.ID);
          if (res?.code === 200) {
            notify("success", res?.message || "Template deleted successfully");
            getCertificateTemplateList(dispatch, pageNum, filterState.filterParams);
          } else {
            const message = Array.isArray(res?.message)
              ? res.message
                .map((err) => (err?.Msg ? err.Msg : JSON.stringify(err)))
                .join(", ")
              : res?.message || "Failed to delete template";
            notify("danger", message);
          }
        } catch (error) {
          notify("danger", error?.message || "Something went wrong while deleting template");
        }
      },
    });
  };

  const toggleStatus = (item) => {
    const { status, ID, name } = item;
    const isActive = status === "ACTIVE";
    const newStatus = isActive ? "INACTIVE" : "ACTIVE";

    showAlert({
      title: `Are you sure you want to ${isActive ? "inactivate" : "activate"} this template?`,
      message: `Template "${name || "Untitled"}" will be marked as ${newStatus.toLowerCase()}.`,
      type: "warning",
      confirmStyle: "deep-blue",
      cancelStyle: "secondary",
      confirmText: "Yes",
      cancelText: "Cancel",
      onConfirm: async (closeAlert) => {
        closeAlert();
        try {
          const formData = new FormData();
          formData.append("status", newStatus);

          const res = await editCertificateTemplate(ID, formData);

          if (res?.code === 200) {
            notify("success", res?.message || `Status updated to ${newStatus.toLowerCase()}`);
            getCertificateTemplateList(dispatch, pageNum, filterState.filterParams);
          } else {
            notify("danger", res?.message || "Failed to update status!");
          }
        } catch (error) {
          notify("danger", "An error occurred while updating status!");
        }
      },
    });
  };

  // const handleDownloadClick = async (parsedTemplate) => {
  //   if (!parsedTemplate || (!parsedTemplate.canvasSize && !parsedTemplate.CanvasSize)) return;

  //   const { width, height } = parsedTemplate.canvasSize || { width: parsedTemplate.CanvasSize?.Width || 707, height: parsedTemplate.CanvasSize?.Height || 500 };
  //   const hiddenCanvasElement = document.createElement("canvas");
  //   hiddenCanvasElement.width = width;
  //   hiddenCanvasElement.height = height;

  //   const canvas = new StaticCanvas(hiddenCanvasElement, {
  //     width,
  //     height,
  //     selection: false,
  //   });

  //   if (parsedTemplate.previewImage) {
  //     const bg = await FabricImage.fromURL(parsedTemplate.previewImage);
  //     bg.set({
  //       left: canvas.width / 2,
  //       top: canvas.height / 2,
  //       originX: "center",
  //       originY: "center",
  //       selectable: false,
  //       evented: false,
  //     });
  //     bg.scaleToWidth(canvas.width);
  //     bg.scaleToHeight(canvas.height);
  //     canvas.backgroundImage = bg;
  //   }

  //   for (const item of parsedTemplate.layout || []) {
  //     const left = (item.x / 100) * canvas.width;
  //     const top = (item.y / 100) * canvas.height;

  //     if (item.type === "text") {
  //       let extraStyles = {};
  //       try {
  //         let srcStr = item.Src ?? item.src;
  //         if (srcStr) {
  //           const jsonStart = srcStr.indexOf('{');
  //           if (jsonStart !== -1) {
  //             srcStr = srcStr.substring(jsonStart);
  //             extraStyles = JSON.parse(srcStr);
  //           }
  //         }
  //       } catch (e) { }

  //       canvas.add(
  //         new Text(item.text || "", {
  //           left,
  //           top,
  //           fontSize: item.fontSize || 16,
  //           fontFamily: item.fontFamily || "Arial",
  //           fontWeight: item.fontWeight || extraStyles.fontWeight || "normal",
  //           fontStyle: item.fontStyle || extraStyles.fontStyle || "normal",
  //           underline: item.underline !== undefined ? item.underline : (extraStyles.underline || false),
  //           fill: item.color || "#000",
  //           angle: item.angle || 0,
  //           textAlign: item.textAlign || "left",
  //           originX: item.OriginX || item.originX || (item.type === "text" ? "center" : "left"),
  //           originY: item.OriginY || item.originY || (item.type === "text" ? "center" : "top"),
  //           scaleX: 1,
  //           scaleY: 1,

  //           // Restore character-level formatting
  //           ...((item.styles || extraStyles.styles) && { styles: item.styles || extraStyles.styles }),

  //           selectable: false,
  //           evented: false,
  //         }),
  //       );
  //     }

  //     if (
  //       item.type === "image" ||
  //       item.type === "signature" ||
  //       item.type === "qr"
  //     ) {
  //       if (!item.src) continue;
  //       const img = await FabricImage.fromURL(item.src);
  //       img.set({
  //         left,
  //         top,
  //         originX: "left",
  //         originY: "top",
  //         angle: item.angle || 0,
  //         selectable: false,
  //         evented: false,
  //       });

  //       const targetWidth = (item.scaleX || 0.5) * 0.32 * canvas.width;
  //       img.scaleToWidth(targetWidth);
  //       canvas.add(img);
  //     }

  //     if (item.type === "shape" && item.shapeType === "line") {
  //       const x1 = item.X1 ?? item.x1 ?? 0;
  //       const y1 = item.Y1 ?? item.y1 ?? 0;
  //       const x2 = item.X2 ?? item.x2 ?? 100;
  //       const y2 = item.Y2 ?? item.y2 ?? 100;
  //       const ox = (item.OriginX ?? item.originX) || "left";
  //       const oy = (item.OriginY ?? item.originY) || "top";
  //       const shape = new Line([x1, y1, x2, y2], {
  //         left,
  //         top,
  //         originX: ox,
  //         originY: oy,
  //         stroke: item.stroke || "#000",
  //         strokeWidth: item.strokeWidth || 2,
  //         strokeDashArray: item.dash || [],
  //         selectable: false,
  //         evented: false,
  //       });
  //       shape.set({
  //         scaleX: item.scaleX || 1,
  //         scaleY: item.scaleY || 1,
  //         angle: item.angle || 0,
  //       });
  //       canvas.add(shape);
  //     }
  //   }

  //   await document.fonts.ready;
  //   canvas.renderAll();

  //   const dataURL = canvas.toDataURL({
  //     format: "png",
  //     quality: 1,
  //     multiplier: 2,
  //   });

  //   const link = document.createElement("a");
  //   link.href = dataURL;
  //   link.download = `${parsedTemplate.name || "certificate-template"}.png`;
  //   document.body.appendChild(link);
  //   link.click();
  //   document.body.removeChild(link);
  //   canvas.dispose();
  // };

  const Option = (props) => {
    return (
      <components.Option {...props}>
        <Badge
          color={props.data.value === "ACTIVE" ? "success" : "warning"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.Option>
    );
  };

  const SingleValue = (props) => {
    return (
      <components.SingleValue {...props}>
        <Badge
          color={props.data.value === "ACTIVE" ? "success" : "warning"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.SingleValue>
    );
  };

  return (
    <>
      <AdminHeader parentName="Learning" />

      <Container fluid className="profile-page mt--6">
        <Row className="justify-content-center">
          <Col xl="12" lg="10">
            <Card className="certificates-card">
              <CardHeader className="d-flex align-items-center justify-content-between">
                <CardTitle tag="h3" className="mb-0 text-default">
                  Templates
                </CardTitle>

                <Button
                  className="btn-deep-blue"
                  onClick={handleOpenStartModal}
                >
                  <i className="fa fa-plus mr-1" />
                  Template
                </Button>
              </CardHeader>
              <CardBody>
                <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap flex-column flex-md-row">
                  <div className="d-flex flex-wrap align-items-center filter-group">
                    <Input
                      type="text"
                      placeholder="Search by name"
                      className="mr-2 mb-2 filter-input-large template-search-input"
                      value={filterState.name}
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleSearch();
                        }
                      }}
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={CERTIFICATE_TEMPLATE_TYPE_OPTIONS}
                      placeholder="Select template type"
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      value={filterState.templateType}
                      onChange={(selected) =>
                        setFilterState((prev) => ({
                          ...prev,
                          templateType: selected,
                        }))
                      }
                    />

                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={STATUS_OPTIONS}
                      placeholder="Select status"
                      components={{ Option, SingleValue }}
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      value={filterState.status}
                      onChange={(selected) =>
                        setFilterState((prev) => ({
                          ...prev,
                          status: selected,
                        }))
                      }
                    />

                    <Button
                      color="default"
                      onClick={handleSearch}
                      className="d-flex align-items-center mb-2 filter-btn btn-deep-blue"
                    >
                      <i className="fa fa-search mr-1" />
                      Search
                    </Button>

                    {filterState.filterParams?.length > 0 && (
                      <Button
                        color="default"
                        className="mb-2 filter-btn btn-simple btn-deep-blue"
                        onClick={handleClear}
                      >
                        <i className="fa fa-times mr-1" />
                        Clear
                      </Button>
                    )}
                  </div>
                </div>

                <div className="mt-2 mb-3 mx-1">
                  {certificateListData?.data?.totalRecords > 0 ? (
                    <>
                      {filterState.filterParams.length > 0 ? (
                        <span className="total-records">
                          Showing{" "}
                          {certificateListData?.data?.totalFilteredRecords} of{" "}
                          {certificateListData?.data?.totalRecords} Records
                        </span>
                      ) : (
                        <span className="total-records">
                          Total Records :{" "}
                          {certificateListData?.data?.totalRecords}
                        </span>
                      )}
                    </>
                  ) : null}
                </div>

                {templateListState.isLoading ? (
                  <div className="loaderContainer">
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </div>
                ) : templateListState.error ? (
                  <div className="loaderContainer error-style">
                    <div className="error-icon">
                      <i className="fa-solid fa-circle-exclamation"></i>
                    </div>
                    <span>{templateListState.error}</span>
                  </div>
                ) : templateListState.noRecords ? (
                  <div className="loaderContainer empty-style">
                    <div className="empty-icon">
                      <i className="fa-solid fa-circle-info"></i>
                    </div>
                    <span>No templates found.</span>
                  </div>
                ) : (
                  <>
                    <Row className="certificates-grid">
                      {(
                        certificateListData?.data?.CertificateTemplate || []
                      ).map((item) => {
                        const parsedTemplate =
                          mapBackendTemplateToFrontend(item);

                        return (
                          <Col
                            key={item.ID}
                            xl="4"
                            lg="6"
                            md="6"
                            className="mb-4"
                          >
                            <Card className="certificate-tile">
                              {/* TOP BAR (ABOVE IMAGE) */}
                              <div className="certificate-top-bar">
                                <Badge
                                  className="certificate-status"
                                  color={
                                    item.status === "ACTIVE"
                                      ? "success"
                                      : "danger"
                                  }
                                >
                                  {item.status === "ACTIVE"
                                    ? "Active"
                                    : "INACTIVE"}
                                </Badge>

                                <div className="certificate-actions-templates">
                                  <Button
                                    size="sm"
                                    className="icon-btn"
                                    onClick={() =>
                                      handleViewClick(parsedTemplate)
                                    }
                                  >
                                    <i className="fa-solid fa-eye" />
                                  </Button>

                                  <Button
                                    size="sm"
                                    className="icon-btn"
                                    onClick={() => {
                                      navigate(
                                        role === "SUPER_ADMIN"
                                          ? `/super-admin/certificate/edit-certificate/${urlEncodedId(item.ID)}`
                                          : `/admin/certificate/edit-certificate/${urlEncodedId(item.ID)}`,
                                      );
                                    }}
                                  >
                                    <i className="fa-solid fa-pen-to-square" />
                                  </Button>

                                  <Button
                                    size="sm"
                                    className="icon-btn text-danger"
                                    onClick={() => handleDeleteClick(item)}
                                  >
                                    <i className="fa fa-trash text-danger cursor" />
                                  </Button>

                                  {/* <Button
                                    size="sm"
                                    className="icon-btn"
                                    onClick={() =>
                                      handleDownloadClick(parsedTemplate)
                                    }
                                  >
                                    <i className="fa-solid fa-download" />
                                  </Button> */}

                                  <UncontrolledTooltip
                                    delay={0}
                                    target={`status${item.ID}`}
                                  >
                                    {item.status === "ACTIVE"
                                      ? "Inactivate"
                                      : "Activate"}
                                  </UncontrolledTooltip>
                                  <div className="status-toggle-wrapper">
                                    <i
                                      id={`status${item.ID}`}
                                      role="button"
                                      tabIndex={0}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleStatus(item);
                                      }}
                                      className={`fas status-toggle-icon ${item.status === "ACTIVE"
                                          ? "fa-toggle-on text-success"
                                          : "fa-toggle-off text-danger"
                                        }`}
                                    ></i>
                                  </div>
                                </div>
                              </div>

                              {/* IMAGE */}
                              <div className="certificate-image-wrapper">
                                <ThumbnailPreview
                                  parsedTemplate={parsedTemplate}
                                />
                              </div>

                              <CardBody className="pb-0">
                                {/* Template Title */}
                                <h4 className="certificate-title">
                                  {parsedTemplate?.name || "Untitled Template"}
                                </h4>

                                {/* Template Details */}
                                <div className="d-flex justify-content-between mt-3">
                                  <div>
                                    <div className="text-muted small">
                                      Created on
                                    </div>
                                    <div className="xs-text">
                                      {item.created_at || "-"}
                                    </div>
                                  </div>

                                  <div className="text-right">
                                    <div className="text-muted small">Type</div>
                                    <div className="xs-text">
                                      {toCamelCase(item.type || "-")}
                                    </div>
                                  </div>
                                </div>
                              </CardBody>
                            </Card>
                          </Col>
                        );
                      })}
                    </Row>

                    {/* PAGINATION */}
                    {Math.ceil(
                      (certificateListData?.data?.totalRecords || 0) /
                      (certificateListData?.data?.noOfRecordsPerPage || 9),
                    ) > 1 && (
                        <Row className="mt-4">
                          <Col>
                            <CustomPagination
                              totalPages={Math.ceil(
                                (certificateListData?.data?.totalRecords || 0) /
                                (certificateListData?.data
                                  ?.noOfRecordsPerPage || 9),
                              )}
                              activePage={pageNum}
                              setPageNum={setPageNum}
                            />
                          </Col>
                        </Row>
                      )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {showStartModal &&
        createPortal(
          <div className="start-layout-overlay">
            <div className="start-layout-modal">
              <div className="d-flex justify-content-end">
                <i
                  className="fa-solid fa-xmark cursor text-muted fa-lg p-2"
                  onClick={() => setShowStartModal(false)}
                  title="Close"
                />
              </div>

              <Row className="mb-4 text-left px-5">
                <Col md={6}>
                  <FormGroup>
                    <Label className="font-control-label">
                      Certificate Template Name{" "}
                      <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="text"
                      placeholder="Enter certificate template name"
                      value={templateMeta.name}
                      maxLength={100}
                      invalid={templateErrors.name}
                      className="certificate-template-name-input"
                      onChange={(e) => {
                        setTemplateMeta(prev => ({ ...prev, name: e.target.value }));
                        if (templateErrors.name) setTemplateErrors(prev => ({ ...prev, name: false }));
                      }}
                      onBlur={() => {
                        if (!templateMeta.name.trim()) {
                          setTemplateErrors((prev) => ({ ...prev, name: true }));
                        }
                      }}
                    />
                    {templateErrors.name && (
                      <div className="text-danger small mt-1">
                        Please enter a certificate template name.
                      </div>
                    )}
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label className="font-control-label">
                      Certificate Template Type{" "}
                      <span className="text-danger">*</span>
                    </Label>
                    <Select
                      options={CERTIFICATE_TEMPLATE_TYPE_OPTIONS}
                      placeholder="Select certificate template type"
                      value={templateMeta.type}
                      onChange={(val) => {
                        setTemplateMeta(prev => ({ ...prev, type: val }));
                        if (templateErrors.type) setTemplateErrors(prev => ({ ...prev, type: false }));
                      }}
                      onBlur={() => {
                        if (!templateMeta.type) {
                          setTemplateErrors((prev) => ({ ...prev, type: true }));
                        }
                      }}
                      styles={getSelectStyle(templateErrors.type)}
                      menuPortalTarget={document.body}
                    />
                    {templateErrors.type && (
                      <div className="text-danger small mt-1">
                        Please select a certificate template type.
                      </div>
                    )}
                  </FormGroup>
                </Col>
              </Row>

              <h2>Select Certificate Size</h2>

              <div className="layout-options">
                <div className="d-flex justify-content-center">
                  <div
                    className="layout-card mr-5 px-5 py-5 shadow-lg"
                    onClick={() => applyCanvasSize("A4")}
                  >
                    <i className="fa-regular fa-file" />
                    <h2>A4</h2>
                  </div>

                  <div
                    className="layout-card px-5 py-5 shadow-lg"
                    onClick={() => applyCanvasSize("A3")}
                  >
                    <i className="fa-regular fa-file" />
                    <h2>A3</h2>
                  </div>
                </div>

                <div className="d-flex justify-content-center">
                  <div
                    className="layout-card mr-5 px-5 py-5 shadow-lg"
                    onClick={() => applyCanvasSize("16:9")}
                  >
                    <i className="fa-regular fa-file" />
                    <h2>16:9</h2>
                  </div>

                  <div
                    className="layout-card px-5 py-5 shadow-lg"
                    onClick={() => applyCanvasSize("4:3")}
                  >
                    <i className="fa-regular fa-file" />
                    <h2>4:3</h2>
                  </div>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Preview Popup Modal */}
      <Modal
        isOpen={showPreview}
        toggle={handlePreviewCancel}
        size="xl"
        centered
        className="preview-modal"
      >
        <ModalHeader toggle={handlePreviewCancel}>Template Preview</ModalHeader>

        <ModalBody className="text-center p-3">
          {previewTemplate &&
            (() => {
              const templateJson = previewTemplate;

              return (
                <div className="preview-modal-content">
                  <ThumbnailPreview parsedTemplate={templateJson} />
                </div>
              );
            })()}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handlePreviewCancel}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default Templates;
